<template>
    <div>
        <project-locked-banner />
        <div class="row gx-3 gy-1 align-items-center">
            <h1 v-bind:class="responsiveClasses.headerContainer">
                {{ header }}
            </h1>
            <div
                v-if="showAdminControls || isAdmin"
                v-bind:class="responsiveClasses.adminButton"
                class="d-flex align-items-center">
                <pc-button-link
                    id="LandingPageButton"
                    v-bind:href="goToButtonUrl">
                    {{ goToButtonLabel }}
                </pc-button-link>
            </div>
            <div
                v-if="showAdminControls || isAdmin"
                v-bind:class="responsiveClasses.jumpProjectWrapper"
                class="col d-flex align-items-center">
                <div class="text-nowrap">
                    <span>{{ goToText }}</span>
                    <pc-temp-space />
                    <span class="fw-bold">{{ projectNumberPrefix }}</span>
                </div>
                <pc-input
                    v-model.trim="jumpProjectInput"
                    alt-label="Project number"
                    name="JumpToProjectNumber"
                    v-bind:external-native-handlers="{ keyup: onjumpProjectKeyUp }"
                    v-bind:class="responsiveClasses.jumpProjectInput"
                    class="ms-1 project-input-min-width">
                    <template v-slot:manualAppend>
                        <button
                            class="btn border btn-secondary input-group-text"
                            v-on:click="jumpProject">
                            {{ goButton }}
                        </button>
                    </template>
                </pc-input>
            </div>
        </div>
        <!-- Must use custom class for margin of alert. Otherwise
            the slide-in transition looks clunky because the bootstrap
            class forces the margin to its full size when it's rendered -->
        <pc-alert
            v-model:shown="showJumpProjectErrorAlert"
            v-bind:severity="jumpProjectAlertSeverity"
            v-bind:duration="jumpProjectAlertDuration"
            v-bind:message="jumpProjectErrorMessage"
            class="project-page-margin-top-1spacer" />
        <slot />
    </div>
</template>
<script>
    import { ALERT_SEVERITY } from "Util/alerts";
    import { defaultAlertDuration } from "Projects/SharedCapsules/_sharedCapsuleBase";
    import projectLockedBanner from "Projects/projectLockedBanner.vue";
    import { KEYS, NAMESPACE } from "./i18n/_projectPageLayout.i18n";
    import { mapI18n } from "Util/i18n";
    import { getResponsiveClassMap } from "Responsive/manager";

    const responsiveConfig = {
        headerContainer: {
            // col-12 col-md-auto mb-3 mb-lg-0
            col: { xs: "auto", md: "auto" },
            mb: { xs: 0, md: 0 }
        },
        adminButton: {
            // col-12 col-md-3 mb-3 mb-lg-0
            col: { xs: "auto", md: "auto" },
            "justify-content": { xs: "center", md: "start" }
        },
        jumpProjectWrapper: {
            // justify-content-lg-end
            "justify-content": { lg: "end" }
        },
        jumpProjectInput: {
            // flex-grow-1 flex-lg-grow-0
            flex: { xs: "grow-1", lg: "grow-0" }
        },
        jumpProjectError: {
            "justify-content": { md: "end" }
        }
    };

    export default {
        components: {
            projectLockedBanner
        },
        inject: {
            viewport: {
                from: "viewport",
                default: null
            }
        },
        props: {
            isAdmin: { type: Boolean, required: false, default: false },
            projectNumberPrefix: { type: String, required: false, default: null },
            projectNumber: { type: String, required: false, default: null },
            goToButtonUrl: { type: String, required: false, default: null },
            showAdminControls: { type: Boolean, required: false, default: false }
        },
        emits: ["jump-to-project"],
        data() {
            return {
                jumpProjectInput: null,
                jumpProjectErrorMessage: "",
                jumpProjectAlertDuration: defaultAlertDuration,
                jumpProjectAlertSeverity: ALERT_SEVERITY.error,
                showJumpProjectErrorAlert: false
            };
        },
        computed: {
            ...mapI18n(KEYS, NAMESPACE),
            header() {
                const prefix = this.isAdmin ? this.adminText : this.viewEditText;
                return `${prefix} ${this.projectNumber}`;
            },
            goToButtonLabel() {
                return this.isAdmin ? this.gotoViewEditButton : this.gotoAdminButton;
            },
            responsiveClasses() {
                return getResponsiveClassMap(this.viewport, responsiveConfig);
            }
        },
        methods: {
            jumpProject() {
                if (!this.jumpProjectInput) {
                    return;
                }

                this.$emit("jump-to-project", this.jumpProjectInput);
            },
            onjumpProjectKeyUp(event) {
                if (event.key == "Enter" || event.keyCode == 13) {
                    this.jumpProject();
                }
            },
            showJumpProjectError(errorMessageText) {
                this.jumpProjectErrorMessage = errorMessageText;
                this.showJumpProjectErrorAlert = true;
            }
        }
    };
</script>
<style lang="scss" scoped>
    .project-page-margin-top-1spacer {
        margin-top: 1rem;
    }
    .project-page-btn-half-min-width {
        min-width: 4rem;
    }
    .project-input-min-width {
        min-width: 5rem;
    }
</style>
